/**
 * @fileoverview Connect a matter's recorded timeline to filters, communications, and explicit activity editors. Backend activity identity owns history; email bodies, audio, and contact-change evidence have separate access/storage boundaries. `ContactChangeDetails.js` and `RecordingTranscript.js` own focused evidence views. Presentation filters and opening a recorded item do not change the underlying business event.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { MultiSelect } from 'primereact/multiselect';
import Button from "../../shared/AppPrimeButton";
import Chip from '../../shared/AppPrimeChip';
import { InputText } from 'primereact/inputtext';
import { useSelector } from 'react-redux';
import moment from 'moment';
import api from '../../../api';
import AddBriefFiledForm from './AddBriefFiledForm';
import AddNoteForm from './AddNoteForm';
import SettlementTrackerForm from './SettlementTrackerForm';
import { AppModal, AppTooltip, AppBadge, AppCalendar } from '../../shared';
import './Activity.css';
import AppIcon from "../../shared/AppIcon";
import ContactChangeDetails from './ContactChangeDetails';
import RecordingTranscript from './RecordingTranscript';

const ACTIVITY_TYPE_LABELS = {
  TextMessage: 'Text Messages',
  'PhoneCall - Answered': 'Phone Calls Answered',
  'PhoneCall - Not Answered': 'Phone Calls Missed',
  Email: 'Emails',
  Note: 'Notes',
  Settlement: 'Settlements',
  FileUpload: 'File Uploads',
  BriefFiled: 'Brief Filed',
  StageChanged: 'Stage Changes',
  SubstageChanged: 'Substage Changes',
  ClientContactChange: 'Contact Changes',
  RuleMatched: 'Rule Matches',
  AttributeRuleMatched: 'Attribute Rule Matches',
  Voicemail: 'Voicemails',
  'Call Recording': 'Call Recordings'
};



function createDefaultFilters() {
  return {
    search: '',
    activityTypes: [],
    tags: [],
    startDate: null,
    endDate: null
  };
}



function formatActivityTypeLabel(activityType) {
  if (!activityType) return 'Unknown';
  if (ACTIVITY_TYPE_LABELS[activityType]) {
    return ACTIVITY_TYPE_LABELS[activityType];
  }
  return activityType.replace(/([a-z])([A-Z])/g, '$1 $2');
}



function formatDateChipLabel(date, prefix) {
  if (!date) return '';
  return `${prefix} ${new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })}`;
}



function formatDisplayDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'No date';
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}



function formatCompactDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'No date';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}



function getActivityTimestamp(value) {
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}



function summarizeSelectedValues(values, options) {
  const labels = values
  .map((value) => options.find((option) => option.value === value)?.label || value)
  .filter(Boolean);

  if (labels.length <= 2) {
    return labels.join(', ');
  }

  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
}



function buildActivitySearchText(activity) {
  return [
  activity.ActivityType,
  formatActivityTypeLabel(activity.ActivityType),
  activity.ClientName,
  activity.ClientContact,
  activity.AttorneyName,
  activity.AttorneyContact,
  activity.PartyMakingOffer,
  activity.Subject,
  activity.Details,
  activity.Location,
  activity.Link,
  ...Object.values(activity.ContactChange?.changes || {}).flatMap((change) => [change.before, change.after]),
  ...(Array.isArray(activity.Tags) ? activity.Tags : [])]

  .filter(Boolean)
  .join(' ')
  .toLowerCase();
}



function truncateText(text, length) {
  if (typeof text === 'string' && text.length > length) {
    return `${text.substring(0, length - 3)}...`;
  }
  return text;
}



function resolveApiHref(path) {
  if (!path) {
    return null;
  }
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const baseUrl = `${api.defaults.baseURL || '/api'}`.replace(/\/$/, '');
  if (path.startsWith('/api/')) {
    return baseUrl.endsWith('/api') ? `${baseUrl}${path.slice(4)}` : `${baseUrl}${path}`;
  }
  return path.startsWith('/') ? `${baseUrl}${path}` : `${baseUrl}/${path}`;
}



function getActivityStyles(activityType, direction, clientName, amount) {
  const baseStyles = {
    TextMessage: {
      inbound: {
        icon: 'pi-comments',
        tone: 'warning'
      },
      outbound: {
        icon: 'pi-comments',
        tone: 'info'
      }
    },
    'PhoneCall - Answered': {
      inbound: {
        icon: 'pi-phone',
        tone: 'warning'
      },
      outbound: {
        icon: 'pi-phone',
        tone: 'info'
      }
    },
    'PhoneCall - Not Answered': {
      inbound: {
        icon: 'pi-phone',
        tone: 'danger'
      },
      outbound: {
        icon: 'pi-phone',
        tone: 'danger'
      }
    },
    Voicemail: {
      inbound: {
        icon: 'pi-microphone',
        tone: 'warning'
      }
    },
    'Call Recording': {
      inbound: {
        icon: 'pi-microphone',
        tone: 'warning'
      },
      outbound: {
        icon: 'pi-microphone',
        tone: 'info'
      }
    },
    Email: {
      inbound: {
        icon: 'pi-envelope',
        tone: 'warning'
      },
      outbound: {
        icon: 'pi-envelope',
        tone: 'info'
      }
    },
    FileUpload: {
      inbound: {
        icon: 'pi-file',
        tone: 'info'
      }
    },
    Settlement: {
      icon: 'pi-dollar',
      tone: 'success'
    },
    Note: {
      icon: 'pi-pencil',
      tone: 'neutral'
    },
    BriefFiled: {
      icon: 'pi-briefcase',
      tone: 'info'
    },
    StageChanged: {
      icon: 'pi-arrows-v',
      tone: 'neutral'
    },
    ClientContactChange: {
      icon: 'pi-user-edit',
      tone: 'info'
    },
    SubstageChanged: {
      icon: 'pi-arrows-v',
      tone: 'neutral'
    },
    RuleMatched: {
      icon: 'pi-exclamation-triangle',
      tone: 'danger'
    },
    AttributeRuleMatched: {
      icon: 'pi-exclamation-circle',
      tone: 'danger'
    }
  };

  let styles = direction ? baseStyles[activityType]?.[direction] : baseStyles[activityType];
  if (!styles && baseStyles[activityType]) {
    styles = baseStyles[activityType].outbound || baseStyles[activityType].inbound || baseStyles[activityType];
  }

  if (!styles) {
    styles = {
      icon: 'pi-question',
      tone: 'neutral'
    };
  }

  let label = '';
  if (activityType === 'Settlement') {
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);

    label = `made a settlement offer for ${formattedAmount}`;
  } else if (activityType === 'Note') {
    label = 'added a note';
  } else if (activityType === 'FileUpload') {
    label = 'uploaded a file';
  } else if (activityType === 'PhoneCall - Answered' || activityType === 'PhoneCall - Not Answered') {
    label = `${direction === 'inbound' ? 'received' : 'made'} a call ${direction === 'inbound' ? 'from' : 'to'} ${clientName}`;
  } else if (activityType === 'Voicemail') {
    label = `received a voicemail from ${clientName}`;
  } else if (activityType === 'Call Recording') {
    label = `${direction === 'inbound' ? 'received' : 'made'} a recorded call ${direction === 'inbound' ? 'from' : 'to'} ${clientName}`;
  } else if (activityType === 'TextMessage') {
    label = `${direction === 'inbound' ? 'received' : 'sent'} a text message ${direction === 'inbound' ? 'from' : 'to'} ${clientName}`;
  } else if (activityType === 'Email') {
    label = `${direction === 'inbound' ? 'received' : 'sent'} an email ${direction === 'inbound' ? 'from' : 'to'} ${clientName}`;
  } else if (activityType === 'BriefFiled') {
    label = 'filed a brief';
  } else if (activityType === 'StageChanged') {
    label = 'updated the case stage';
  } else if (activityType === 'ClientContactChange') {
    label = `recorded a contact change for ${clientName}`;
  } else if (activityType === 'SubstageChanged') {
    label = 'updated the case substage';
  } else if (activityType === 'RuleMatched') {
    label = 'triggered a rule match';
  } else if (activityType === 'AttributeRuleMatched') {
    label = 'triggered an attribute rule match';
  } else {
    label = formatActivityTypeLabel(activityType);
  }

  return {
    ...styles,
    label
  };
}



function LinkExtractor({ linkString }) {
  if (typeof linkString !== 'string') {
    return null;
  }

  const fileMatch = linkString.match(/file\/d\/([a-zA-Z0-9_-]+)/);
  const folderMatch = linkString.match(/folders\/([a-zA-Z0-9_-]+)/);

  const fileID = fileMatch ? fileMatch[1] : null;
  const folderID = folderMatch ? folderMatch[1] : null;

  return (
    <div className="activity-link-group">
            {fileID &&
      <a
        href={`https://drive.google.com/file/d/${fileID}/view`}
        target="_blank"
        rel="noopener noreferrer">

                    Open File
                </a>}

            {folderID &&
      <a
        href={`https://drive.google.com/drive/folders/${folderID}`}
        target="_blank"
        rel="noopener noreferrer">

                    Open Folder
                </a>}

        </div>);

}



function ActivityPill({ tone = 'neutral', children }) {
  return (
    <span className={`activity-pill activity-pill--${tone}`}>
            {children}
        </span>);

}



function Activity({ matterNumber }) {
  const matter = useSelector((state) => state.caseDetails.matter);
  const [allActivities, setAllActivities] = useState([]);
  const [filters, setFilters] = useState(createDefaultFilters);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [detailsForModal, setDetailsForModal] = useState('');
  const [emailDetailVisible, setEmailDetailVisible] = useState(false);
  const [emailDetail, setEmailDetail] = useState(null);
  const [emailDetailError, setEmailDetailError] = useState('');
  const [isLoadingEmailDetail, setIsLoadingEmailDetail] = useState(false);

  const [noteForm, setNoteForm] = useState({
    visible: false,
    isEditing: false,
    currentNote: null,
    currentNoteId: null
  });

  const [settlementForm, setSettlementForm] = useState({
    visible: false,
    isEditing: false,
    currentSettlement: {
      date: new Date()
    },
    currentSettlementId: null
  });

  const [briefFiledForm, setBriefFiledForm] = useState({
    visible: false,
    isEditing: false,
    currentBriefFiled: {
      date: new Date()
    },
    currentBriefFiledId: null
  });

  const fetchActivities = useCallback(

  () => {
    api.get(`/activity/${matterNumber}`)
    .then((response) => {
      setAllActivities(Array.isArray(response.data) ? response.data : []);
    })
    .catch((error) => {
      console.error('Error fetching activities:', error);
    });
  }, [matterNumber]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const activityTypeOptions = useMemo(() => {
    return [...new Set(allActivities.map((item) => item.ActivityType).filter(Boolean))]
    .sort((a, b) => formatActivityTypeLabel(a).localeCompare(formatActivityTypeLabel(b)))
    .map((activityType) => ({
      label: formatActivityTypeLabel(activityType),
      value: activityType
    }));
  }, [allActivities]);

  const tagOptions = useMemo(() => {
    const tags = new Set();
    allActivities.forEach((activityItem) => {
      if (Array.isArray(activityItem.Tags)) {
        activityItem.Tags.forEach((tag) => {
          if (tag) {
            tags.add(tag);
          }
        });
      }
    });

    return [...tags]
    .sort((a, b) => a.localeCompare(b))
    .map((tag) => ({ label: tag, value: tag }));
  }, [allActivities]);

  const filteredActivities = useMemo(() => {
    const trimmedSearch = filters.search.trim().toLowerCase();

    return allActivities
    .filter(

    (activityItem) => {
      if (trimmedSearch && !buildActivitySearchText(activityItem).includes(trimmedSearch)) {
        return false;
      }

      if (filters.activityTypes.length > 0 && !filters.activityTypes.includes(activityItem.ActivityType)) {
        return false;
      }

      if (
      filters.tags.length > 0 &&
      !(Array.isArray(activityItem.Tags) && activityItem.Tags.some((tag) => filters.tags.includes(tag))))
      {
        return false;
      }

      if (filters.startDate || filters.endDate) {
        const activityDate = moment(activityItem.Date);
        if (!activityDate.isValid()) {
          return false;
        }

        if (filters.startDate && activityDate.isBefore(moment(filters.startDate).startOf('day'))) {
          return false;
        }

        if (filters.endDate && activityDate.isAfter(moment(filters.endDate).endOf('day'))) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => moment(b.Date).valueOf() - moment(a.Date).valueOf());
  }, [allActivities, filters]);

  const activitySummary = useMemo(

  () => {
    const visibleTypeCounts = new Map();
    const visibleTagCounts = new Map();
    let latestOverallTimestamp = null;

    allActivities.forEach((activityItem) => {
      const timestamp = getActivityTimestamp(activityItem.Date);
      if (timestamp !== null && (latestOverallTimestamp === null || timestamp > latestOverallTimestamp)) {
        latestOverallTimestamp = timestamp;
      }
    });

    filteredActivities.forEach((activityItem) => {
      const typeKey = activityItem.ActivityType || 'Unknown';
      visibleTypeCounts.set(typeKey, (visibleTypeCounts.get(typeKey) || 0) + 1);

      if (Array.isArray(activityItem.Tags) && activityItem.Tags.length > 0) {
        activityItem.Tags.filter(Boolean).forEach((tag) => {
          visibleTagCounts.set(tag, (visibleTagCounts.get(tag) || 0) + 1);
        });
      }
    });

    const visibleTypeBreakdown = Array.from(visibleTypeCounts.entries())
    .sort((a, b) => b[1] - a[1] || formatActivityTypeLabel(a[0]).localeCompare(formatActivityTypeLabel(b[0])))
    .map(([activityType, count]) => {
      const { icon, tone } = getActivityStyles(activityType);
      return {
        activityType,
        label: formatActivityTypeLabel(activityType),
        count,
        icon,
        tone
      };
    });

    const visibleTagBreakdown = Array.from(visibleTagCounts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([tag, count]) => ({ tag, count }));

    return {
      latestOverallTimestamp,
      visibleTypeBreakdown,
      visibleTagBreakdown
    };
  }, [allActivities, filteredActivities]);

  const onFilterChange = useCallback(

  (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const onClearAll = useCallback(

  () => {
    setFilters(createDefaultFilters());
  }, []);

  const openDetailsModal =

  (details) => {
    setDetailsForModal(details);
    setDetailsModalVisible(true);
  };

  const openEmailDetailModal = useCallback(

  async (activity) => {
    setEmailDetailVisible(true);
    setEmailDetail(null);
    setEmailDetailError('');
    setIsLoadingEmailDetail(true);

    try {
      const response = await api.get(`/activity/email/${activity.id}`);
      setEmailDetail(response.data || null);
    } catch (error) {
      console.error('Error fetching email details:', error);
      setEmailDetailError('Unable to load the stored email right now.');
    } finally {
      setIsLoadingEmailDetail(false);
    }
  }, []);

  const openAddNoteForm =

  () => {
    setNoteForm({
      visible: true,
      isEditing: false,
      currentNote: null
    });
  };

  const openAddNoteFormInEditMode =

  (note, id, date) => {
    setNoteForm({
      visible: true,
      isEditing: true,
      currentNote: note,
      currentNoteId: id,
      date: date
    });
  };

  const openSettlementForm =

  () => {
    setSettlementForm({
      visible: true,
      isEditing: false,
      currentSettlement: {
        date: new Date()
      }
    });
  };

  const openSettlementFormInEditMode =

  (settlement, id) => {
    setSettlementForm({
      visible: true,
      isEditing: true,
      currentSettlement: settlement,
      currentSettlementId: id
    });
  };

  const openBriefFiledForm =

  () => {
    setBriefFiledForm({
      visible: true,
      isEditing: false,
      currentBriefFiled: {
        date: new Date()
      }
    });
  };

  const openBriefFiledFormInEditMode =

  (briefFiled, id) => {
    setBriefFiledForm({
      visible: true,
      isEditing: true,
      currentBriefFiled: briefFiled,
      currentBriefFiledId: id
    });
  };

  const partyMakingOfferOptions = useMemo(() => {
    const options = [
    ...(matter?.defendants || []).map((defendant) => defendant.PartyName).filter(Boolean),
    ...(matter?.clients || []).map((client) => client.ClientName).filter(Boolean)];

    return [...new Set(options)];
  }, [matter]);

  const defaultAttorneyName = matter?.attorneys?.[0]?.Name || 'Attorney';
  const hasVisibleActivities = filteredActivities.length > 0;
  return (
    <div className="case-activity">
            <section className="activity-overview">
                <div className="activity-overview-copy">
                    <div className="activity-overview-eyebrow">Activity</div>
                    <h2 className="activity-overview-title">Case timeline</h2>
                    <p className="activity-overview-description">
                        Communications, filings, notes, and matter events in one place, with filters that keep the current view easy to narrow.
                    </p>
                </div>

                <div className="activity-toolbar-actions">
                    <Button
            label="Settlement"
            icon="pi pi-dollar"
            className="p-button-sm p-button-outlined activity-action-button"
            onClick={openSettlementForm} />

                    <Button
            label="Brief Filed"
            icon="pi pi-briefcase"
            className="p-button-sm p-button-outlined activity-action-button"
            onClick={openBriefFiledForm} />

                    <Button
            label="Note"
            icon="pi pi-pencil"
            className="p-button-sm p-button-outlined activity-action-button"
            onClick={openAddNoteForm} />

                </div>
            </section>

            <ActivityFilters
        filters={filters}
        onFilterChange={onFilterChange}
        onClearAll={onClearAll}
        filteredCount={filteredActivities.length}
        totalCount={allActivities.length}
        activityTypeOptions={activityTypeOptions}
        tagOptions={tagOptions} />


            <SettlementTrackerForm
        settlementForm={settlementForm}
        setSettlementForm={setSettlementForm}
        matterNumber={matterNumber}
        fetchActivities={fetchActivities}
        partyMakingOfferOptions={partyMakingOfferOptions} />

            <AddNoteForm
        noteForm={noteForm}
        setNoteForm={setNoteForm}
        matterNumber={matterNumber}
        fetchActivities={fetchActivities} />

            <AddBriefFiledForm
        briefFiledForm={briefFiledForm}
        setBriefFiledForm={setBriefFiledForm}
        matterNumber={matterNumber}
        fetchActivities={fetchActivities} />


            <div className="activity-panel-grid">
                <section className="activity-panel">
                    <div className="activity-panel-header">
                        <div>
                            <h3 className="activity-panel-title">Timeline</h3>
                            <p className="activity-panel-description">
                                Recent activity first, with related tags and quick access to editable records.
                            </p>
                        </div>
                        <div className="activity-panel-count">{filteredActivities.length}</div>
                    </div>

                    {hasVisibleActivities ?
          <ul className="activity-timeline">
                            {filteredActivities.map(

                            (act, index) => {
              const contactName = act.ClientName || act.ClientContact || 'Contact';
              const styles = getActivityStyles(act.ActivityType, act.Direction, contactName, act.Amount) || {};
              const { icon, tone, label } = styles;
              const relativeDateLabel = getActivityTimestamp(act.Date) !== null ? moment(act.Date).fromNow() : 'No date';
              const directionTone = act.Direction === 'inbound' ? 'warning' : 'info';
              const directionLabel = act.Direction ?
              `${act.Direction.charAt(0).toUpperCase()}${act.Direction.slice(1)}` :
              null;
              const name = act.AttorneyName || act.PartyMakingOffer || act.AttorneyContact || defaultAttorneyName;
              const attorneyTooltipText = act.PartyMakingOffer ? null : act.AttorneyContact;
              const clientTooltipText = act.ClientContact;
              let details = null;

              if (act.ActivityType === 'ClientContactChange') {
                details = <ContactChangeDetails change={act.ContactChange} />;
              } else if (act.ActivityType === 'FileUpload') {
                details = <LinkExtractor linkString={act.Details} />;
              } else if (act.ActivityType === 'Note') {
                details =
                <button
                  type="button"
                  className="activity-detail-link"
                  onClick={() => openAddNoteFormInEditMode(act.Details, act.id, act.Date)}>

                                            <span>{truncateText(act.Details, 180) || 'Open note'}</span>
                                        </button>;

              } else if (act.ActivityType === 'Settlement') {
                details =
                <button
                  type="button"
                  className="activity-detail-link"
                  onClick={() => openSettlementFormInEditMode(act, act.id)}>

                                            <span>{truncateText(act.Details, 180) || 'Open settlement record'}</span>
                                        </button>;

              } else if (act.ActivityType === 'Email') {
                details =
                <button
                  type="button"
                  className="activity-detail-link"
                  onClick={() => openEmailDetailModal(act)}>

                                            <div className="activity-detail-title">{truncateText(act.Subject, 180) || 'Open email details'}</div>
                                            {act.Details &&
                  <div className="activity-detail-body">{truncateText(act.Details, 180)}</div>}

                                            <div className="activity-email-meta-row">
                                                {act.EmailAttachmentCount > 0 &&
                    <AppBadge value={`${act.EmailAttachmentCount} attachment${act.EmailAttachmentCount === 1 ? '' : 's'}`} severity="secondary" />}

                                                {act.EmailDetailPruned &&
                    <AppBadge value="Pruned from hot storage" severity="warning" />}

                                            </div>
                                        </button>;

              } else if (act.ActivityType === 'Voicemail' || act.ActivityType === 'Call Recording') {
                details = act.RecordingAvailable ?
                <div className="activity-recording-player">
                                            <audio
                    controls
                    preload="metadata"
                    src={resolveApiHref(act.RecordingPlaybackUrl)}
                    aria-label={`${formatActivityTypeLabel(act.ActivityType)} audio`} />

                                            {Number.isFinite(Number(act.RecordingDurationSeconds)) &&
                  <span>{Math.max(0, Number(act.RecordingDurationSeconds))} sec</span>}

                                            <RecordingTranscript activity={act} />
                                        </div> :
                <span className="activity-recording-unavailable">Recording unavailable</span>;

              } else if (act.ActivityType === 'BriefFiled') {
                details =
                <button
                  type="button"
                  className="activity-detail-link"
                  onClick={() => openBriefFiledFormInEditMode(act, act.id)}>

                                            {act.Location && <div className="activity-detail-title">{act.Location}</div>}
                                            {act.Link && <div className="activity-detail-body">{act.Link}</div>}
                                            {!act.Location && !act.Link &&
                  <span>Open brief record</span>}

                                        </button>;

              } else if (act.Details) {
                details =
                <button
                  type="button"
                  className="activity-detail-link"
                  onClick={() => openDetailsModal(act.Details)}>

                                            <span>{truncateText(act.Details, 180)}</span>
                                        </button>;

              }

              return (
                <li key={act.id || index} className="activity-timeline-item" data-tone={tone}>
                                        <div className="activity-timeline-marker">
                                            <span className="activity-timeline-icon">
                                                <AppIcon icon={`pi ${icon}`} aria-hidden="true" />
                                            </span>
                                        </div>

                                        <div className="activity-timeline-main">
                                            <div className="activity-timeline-topline">
                                                <div className="activity-timeline-copy">
                                                    <AppTooltip content={attorneyTooltipText} position="right">
                                                        <span className="activity-timeline-name">{name}</span>
                                                    </AppTooltip>
                                                    <AppTooltip content={clientTooltipText} position="right">
                                                        <div className="activity-timeline-label">{label}</div>
                                                    </AppTooltip>
                                                </div>

                                                <div className="activity-timeline-date">
                                                    <div className="activity-timeline-relative">{relativeDateLabel}</div>
                                                    <div className="activity-timeline-absolute">{formatDisplayDate(act.Date)}</div>
                                                </div>
                                            </div>

                                            <div className="activity-timeline-meta">
                                                <ActivityPill tone={tone}>
                                                    {formatActivityTypeLabel(act.ActivityType)}
                                                </ActivityPill>
                                                {directionLabel &&
                      <ActivityPill tone={directionTone}>
                                                        {directionLabel}
                                                    </ActivityPill>}

                                                {Array.isArray(act.Tags) && act.Tags.filter(Boolean).map((tag) =>
                      <ActivityPill key={`${act.id || index}-${tag}`} tone="neutral">
                                                        {tag}
                                                    </ActivityPill>
                      )}
                                            </div>

                                            {details &&
                    <div className="activity-timeline-detail">
                                                    {details}
                                                </div>}

                                        </div>
                                    </li>);

            })}
                        </ul> :

          <div className="activity-panel-empty">
                            <div className="activity-empty-icon">
                                <AppIcon icon="pi pi-inbox" aria-hidden="true" />
                            </div>
                            <div className="activity-empty-title">
                                {allActivities.length === 0 ? 'No activity found' : 'No activity matches the current filters'}
                            </div>
                            <div className="activity-empty-copy">
                                {allActivities.length === 0 ?
              'This matter does not have any recorded activity yet.' :
              'Adjust or clear the filters to bring more activity back into view.'}
                            </div>
                        </div>}

                </section>

                <aside className="activity-panel activity-panel--sidebar">
                    <div className="activity-panel-header">
                        <div>
                            <h3 className="activity-panel-title">View Summary</h3>
                            <p className="activity-panel-description">
                                What the current filters surface most often.
                            </p>
                        </div>
                        <div className="activity-panel-count">{activitySummary.visibleTypeBreakdown.length}</div>
                    </div>

                    <div className="activity-sidebar-section">
                        <div className="activity-sidebar-heading">Activity Mix</div>
                        {activitySummary.visibleTypeBreakdown.length > 0 ?
            <ul className="activity-breakdown-list">
                                {activitySummary.visibleTypeBreakdown.slice(0, 6).map((entry) =>
              <li key={entry.activityType} className="activity-breakdown-item">
                                        <div className="activity-breakdown-label">
                                            <span className="activity-breakdown-icon" data-tone={entry.tone}>
                                                <AppIcon icon={`pi ${entry.icon}`} aria-hidden="true" />
                                            </span>
                                            <span>{entry.label}</span>
                                        </div>
                                        <span className="activity-breakdown-count">{entry.count}</span>
                                    </li>
              )}
                            </ul> :

            <div className="activity-sidebar-empty">No visible activity types in the current view.</div>}

                    </div>

                    <div className="activity-sidebar-divider" />

                    <div className="activity-sidebar-section">
                        <div className="activity-sidebar-heading">Tags In View</div>
                        {activitySummary.visibleTagBreakdown.length > 0 ?
            <div className="activity-tag-cloud">
                                {activitySummary.visibleTagBreakdown.slice(0, 8).map((entry) =>
              <span key={entry.tag} className="activity-tag-pill">
                                        <span>{entry.tag}</span>
                                        <strong>{entry.count}</strong>
                                    </span>
              )}
                            </div> :

            <div className="activity-sidebar-empty">No tagged activity in the current view.</div>}

                    </div>

                    <div className="activity-sidebar-divider" />

                    <div className="activity-sidebar-section">
                        <div className="activity-sidebar-heading">Latest Recorded</div>
                        <div className="activity-latest-card">
                            <div className="activity-latest-value">
                                {activitySummary.latestOverallTimestamp ? formatCompactDate(activitySummary.latestOverallTimestamp) : 'No activity'}
                            </div>
                            <div className="activity-latest-meta">
                                {activitySummary.latestOverallTimestamp ?
                `${moment(activitySummary.latestOverallTimestamp).fromNow()} across all records` :
                'No dated activity has been recorded yet'}
                            </div>
                        </div>
                    </div>
                </aside>
            </div>

            <DetailsModal
        details={detailsForModal}
        detailsModalVisible={detailsModalVisible}
        setDetailsModalVisible={setDetailsModalVisible} />

            <EmailDetailsModal
        visible={emailDetailVisible}
        onClose={() => setEmailDetailVisible(false)}
        emailDetail={emailDetail}
        isLoading={isLoadingEmailDetail}
        error={emailDetailError} />

        </div>);

}

export default Activity;



function DetailsModal({ details, detailsModalVisible, setDetailsModalVisible }) {
  return (
    <AppModal
      title="Activity Details"
      description="Expanded text for the selected activity record."
      visible={detailsModalVisible}
      width="min(92vw, 760px)"
      onClose={() => setDetailsModalVisible(false)}
      className="case-details-modal">

            <div className="case-details-modal__form">
                <section className="case-details-modal__section">
                    <div className="case-details-modal__section-header">
                        <div>
                            <h3 className="case-details-modal__section-title">Recorded Details</h3>
                            <p className="case-details-modal__section-description">
                                Review the full text exactly as it was stored with the activity entry.
                            </p>
                        </div>
                    </div>
                    <div className="case-details-modal__section-body">
                        <p className="case-details-modal__details-copy">{details || 'No additional details were recorded for this activity.'}</p>
                    </div>
                </section>
            </div>
        </AppModal>);

}



function EmailDetailsModal({ visible, onClose, emailDetail, isLoading, error }) {
  const sanitizedHtml = useMemo(() => {
    if (!emailDetail?.body_html || emailDetail?.is_pruned) {
      return null;
    }
    return DOMPurify.sanitize(emailDetail.body_html);
  }, [emailDetail]);

  return (
    <AppModal
      title="Email Details"
      description="Full email content and attachments for the selected activity."
      visible={visible}
      width="min(96vw, 920px)"
      onClose={onClose}
      className="case-details-modal">

            <div className="case-details-modal__form">
                {isLoading ?
        <section className="case-details-modal__section">
                        <div className="case-details-modal__section-body">
                            <p className="case-details-modal__details-copy">Loading stored email content...</p>
                        </div>
                    </section> :
        error ?
        <section className="case-details-modal__section">
                        <div className="case-details-modal__section-body">
                            <p className="case-details-modal__details-copy">{error}</p>
                        </div>
                    </section> :

        <>
                        <section className="case-details-modal__section">
                            <div className="case-details-modal__section-header">
                                <div>
                                    <h3 className="case-details-modal__section-title">{emailDetail?.subject || 'Email details'}</h3>
                                    <p className="case-details-modal__section-description">
                                        {emailDetail?.received_date ?
                  `Received ${formatDisplayDate(emailDetail.received_date)}` :
                  'Stored email metadata'}
                                    </p>
                                </div>
                            </div>
                            <div className="case-details-modal__section-body">
                                <div className="activity-email-header-grid">
                                    <div><strong>From:</strong> {emailDetail?.from_address || 'Unavailable'}</div>
                                    <div><strong>To:</strong> {emailDetail?.to_address || 'Unavailable'}</div>
                                    {emailDetail?.cc_address && <div><strong>CC:</strong> {emailDetail.cc_address}</div>}
                                    {emailDetail?.bcc_address && <div><strong>BCC:</strong> {emailDetail.bcc_address}</div>}
                                </div>
                                {emailDetail?.prune_notice &&
              <div className="activity-email-pruned-notice">
                                        {emailDetail.prune_notice}
                                    </div>}

                                {!emailDetail?.available &&
              <div className="activity-email-pruned-notice">
                                        Full email content is not stored for this activity yet. The legacy activity preview is shown below.
                                    </div>}

                            </div>
                        </section>

                        <section className="case-details-modal__section">
                            <div className="case-details-modal__section-header">
                                <div>
                                    <h3 className="case-details-modal__section-title">Message Body</h3>
                                    <p className="case-details-modal__section-description">
                                        The Activity pane prefers stored HTML when available and falls back to plain text.
                                    </p>
                                </div>
                            </div>
                            <div className="case-details-modal__section-body">
                                {sanitizedHtml ?
              <div
                className="activity-email-body activity-email-body--html"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }} /> :


              <div className="activity-email-body">
                                        {emailDetail?.body_text || emailDetail?.legacy_details || 'No email body is currently available.'}
                                    </div>}

                            </div>
                        </section>

                        <section className="case-details-modal__section">
                            <div className="case-details-modal__section-header">
                                <div>
                                    <h3 className="case-details-modal__section-title">Attachments</h3>
                                    <p className="case-details-modal__section-description">
                                        Stored email attachments remain available here until the hot copy is pruned.
                                    </p>
                                </div>
                            </div>
                            <div className="case-details-modal__section-body">
                                {Array.isArray(emailDetail?.attachments) && emailDetail.attachments.length > 0 ?
              <div className="activity-email-attachments">
                                        {emailDetail.attachments.map((attachment) =>
                <div key={attachment.id} className="activity-email-attachment-row">
                                                <div>
                                                    <div className="activity-email-attachment-name">{attachment.filename}</div>
                                                    <div className="activity-email-attachment-meta">
                                                        {attachment.mime_type || 'Attachment'}
                                                        {attachment.size_bytes ? ` - ${Math.round(attachment.size_bytes / 1024)} KB` : ''}
                                                    </div>
                                                </div>
                                                <div className="activity-email-attachment-actions">
                                                    {attachment.is_pruned ?
                    <AppBadge value="Pruned" severity="warning" /> :

                    <>
                                                            <a href={resolveApiHref(attachment.view_url)} target="_blank" rel="noreferrer">Open</a>
                                                            <a href={resolveApiHref(attachment.download_url)} target="_blank" rel="noreferrer">Download</a>
                                                        </>}

                                                </div>
                                            </div>
                )}
                                    </div> :

              <p className="case-details-modal__details-copy">No attachments were stored for this email.</p>}

                            </div>
                        </section>
                    </>}

            </div>
        </AppModal>);

}



function ActivityFilters({
  filters,
  onFilterChange,
  onClearAll,
  filteredCount,
  totalCount,
  activityTypeOptions,
  tagOptions
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  const activeFilters = useMemo(

  () => {
    const active = [];

    if (filters.search.trim()) {
      active.push({
        key: 'search',
        label: `"${filters.search.trim()}"`,
        icon: 'pi pi-search'
      });
    }

    if (filters.activityTypes.length > 0) {
      active.push({
        key: 'activityTypes',
        label: summarizeSelectedValues(filters.activityTypes, activityTypeOptions),
        icon: 'pi pi-list'
      });
    }

    if (filters.tags.length > 0) {
      active.push({
        key: 'tags',
        label: summarizeSelectedValues(filters.tags, tagOptions),
        icon: 'pi pi-tag'
      });
    }

    if (filters.startDate) {
      active.push({
        key: 'startDate',
        label: formatDateChipLabel(filters.startDate, 'From'),
        icon: 'pi pi-calendar'
      });
    }

    if (filters.endDate) {
      active.push({
        key: 'endDate',
        label: formatDateChipLabel(filters.endDate, 'Through'),
        icon: 'pi pi-calendar'
      });
    }

    return active;
  }, [filters, activityTypeOptions, tagOptions]);

  const hasActiveFilters = activeFilters.length > 0;

  const handleRemoveFilter =

  (filterKey) => {
    if (filterKey === 'search') {
      onFilterChange('search', '');
      return;
    }

    if (filterKey === 'activityTypes' || filterKey === 'tags') {
      onFilterChange(filterKey, []);
      return;
    }

    onFilterChange(filterKey, null);
  };

  return (
    <section className="activity-filters-panel">
            <div className="activity-filters-header">
                <div className="activity-filters-title-section">
                    <AppIcon icon="pi pi-filter activity-filters-icon" />
                    <div>
                        <h2 className="activity-filters-title">Filters</h2>
                        <p className="activity-filters-subtitle">Search, narrow, and focus the current timeline view.</p>
                    </div>
                    {hasActiveFilters &&
          <span className="activity-filter-count-badge">
                            {activeFilters.length}
                        </span>}

                </div>

                <div className="activity-filters-actions">
                    <span className="activity-filter-results-count">
                        Showing {filteredCount} of {totalCount}
                    </span>

                    {hasActiveFilters &&
          <Button
            label="Clear All"
            icon="pi pi-times"
            className="p-button-text p-button-sm activity-clear-filters-btn"
            onClick={onClearAll} />}



                    <Button
            icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
            className="p-button-text p-button-sm activity-filters-toggle-btn"
            onClick={() => setIsExpanded(!isExpanded)} />

                </div>
            </div>

            {isExpanded &&
      <div className="activity-filters-content">
                    {hasActiveFilters &&
        <div className="activity-active-filters-chips">
                            {activeFilters.map((filter) =>
          <Chip
            key={filter.key}
            label={filter.label}
            icon={filter.icon}
            removable
            onRemove={() => handleRemoveFilter(filter.key)}
            className="activity-filter-chip" />

          )}
                        </div>}


                    <div className="activity-filters-grid">
                        <div className="activity-filter-group" data-filter-active={!!filters.search}>
                            <label htmlFor="activity-search-filter" className="activity-filter-label">
                                <AppIcon icon="pi pi-search activity-filter-label-icon" />
                                SEARCH
                            </label>
                            <InputText
              id="activity-search-filter"
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              placeholder="Search names, subjects, details, tags..."
              className={filters.search ? 'filter-active' : ''} />

                        </div>

                        <div className="activity-filter-group" data-filter-active={filters.activityTypes.length > 0}>
                            <label htmlFor="activity-type-filter" className="activity-filter-label">
                                <AppIcon icon="pi pi-list activity-filter-label-icon" />
                                ACTIVITY TYPE
                            </label>
                            <MultiSelect
              id="activity-type-filter"
              value={filters.activityTypes}
              options={activityTypeOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => onFilterChange('activityTypes', e.value || [])}
              filter
              placeholder="All Activity Types"
              disabled={activityTypeOptions.length === 0}
              className={filters.activityTypes.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="activity-filter-group" data-filter-active={filters.tags.length > 0}>
                            <label htmlFor="activity-tag-filter" className="activity-filter-label">
                                <AppIcon icon="pi pi-tag activity-filter-label-icon" />
                                TAGS
                            </label>
                            <MultiSelect
              id="activity-tag-filter"
              value={filters.tags}
              options={tagOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => onFilterChange('tags', e.value || [])}
              filter
              placeholder="All Tags"
              disabled={tagOptions.length === 0}
              className={filters.tags.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="activity-filter-group" data-filter-active={!!filters.startDate}>
                            <label className="activity-filter-label">
                                <AppIcon icon="pi pi-calendar activity-filter-label-icon" />
                                FROM
                            </label>
                            <AppCalendar
              value={filters.startDate}
              onChange={(e) => onFilterChange('startDate', e.value || null)}
              className={filters.startDate ? 'filter-active' : ''} />

                        </div>

                        <div className="activity-filter-group" data-filter-active={!!filters.endDate}>
                            <label className="activity-filter-label">
                                <AppIcon icon="pi pi-calendar activity-filter-label-icon" />
                                THROUGH
                            </label>
                            <AppCalendar
              value={filters.endDate}
              onChange={(e) => onFilterChange('endDate', e.value || null)}
              className={filters.endDate ? 'filter-active' : ''} />

                        </div>
                    </div>
                </div>}

        </section>);

}
